import axios from 'axios';
import Hex from 'crypto-js/enc-hex';
import Utf8 from 'crypto-js/enc-utf8';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { Dispatch } from 'redux';
import config from '../../../../app.config.json';
import { API_URL } from '../services/axios';
import { API_DATE_FORMAT } from '../services/dayjs';
import logging from '../services/logging';
import * as storage from '../services/storage';
import { setItem, getItem, removeItem } from '../services/storage.secure';
import { NetworkContext } from '../types/network';

// updateAuthToken is allowed one retry attempt
// After a DB import in the dev env, deviceIds from previous days will become invalid
// Retry allows the app to fail once, clear the deviceId and try again
let tokenRetryAttempts = 1;

export const TOKEN_ID = 'token';
export const DEVICE_ID = 'deviceIdv4';
export const PROFILE_KEY = 'profile_unsafe';

// Disabled debounce during tests to ensure failed requests aren't carried into subsequent tests
const DEBOUNCE_TIME = process?.env?.JEST_WORKER_ID ? 0 : 2000;

interface Token {
  authToken: string;
  authTokenExpires: string;
  currency: string;
  loggedIn: boolean;
  emailHashes?: string[];
}

export interface TokenParams {
  email?: string;
  password?: string;
  aAccessToken?: string;
  aBundleId?: string;
  fbUserId?: string;
  fbAccessToken?: string;
  gAccessToken?: string;
  logout?: boolean;
  inviteId?: number;
  inviteToken?: string;
}

export interface TokenResponse {
  token: {
    authToken: string | null;
    authTokenExpires: string;
    loggedIn: boolean;
    currency: string;
  };
  error?: string;
}

const updateProfile = async (token: Token, ctx?: NetworkContext) => {
  const profile = await storage.getItem(PROFILE_KEY, ctx);

  if (!token.loggedIn && profile) {
    return storage.removeItem(PROFILE_KEY, ctx);
  }

  return null;
};

export const getDeviceId = async (ctx?: NetworkContext) => {
  const deviceId = await getItem(DEVICE_ID, ctx);

  if (deviceId) {
    return deviceId;
  }

  try {
    const result = await axios({
      method: 'POST',
      url: `${API_URL}/device`,
      data: {
        clientId: config.clientId,
      },
    });

    await setItem(DEVICE_ID, result.data.deviceId, ctx);

    return result.data.deviceId;
  } catch (error: any) {
    logging.logError(error);

    return '';
  }
};

export const updateAuthToken = async (
  tokenParams?: TokenParams,
  ctx?: NetworkContext,
  dispatch?: Dispatch,
): Promise<TokenResponse> => {
  try {
    // eslint-disable-next-line
    const deviceId = ctx ? await getDeviceId(ctx) : await getDeviceIdDebounced(ctx);

    const result = await axios({
      method: 'POST',
      url: `${API_URL}/token`,
      data: {
        clientId: config.clientId,
        deviceId,
        ...tokenParams,
      },
    });

    updateProfile(result.data, ctx);

    const token = {
      authToken: result.data.token,
      authTokenExpires: result.data.expires,
      currency: result.data.currency,
      loggedIn: result.data.loggedIn,
      emailHashes: result.data.emailHashes,
    };

    logging.logCrumb({
      message: 'New auth token',
      metaData: {
        expires: result.data.expires,
        loggedIn: result.data.loggedIn,
      },
    });

    await setItem(TOKEN_ID, token, ctx);

    // getAuthTokenDebounced must be reassigned to prevent getAuthToken returning previous token via debounce
    // eslint-disable-next-line
    getAuthTokenDebounced = debounce(getAuthToken, 3000, {
      leading: true,
      trailing: false,
    });

    return { token };
  } catch (error: any) {
    if (error && error.response && error.response.status !== 400) {
      logging.logError(error);
    }

    // Clear deviceId if invalid one is used
    if (error && error.response && error.response.status === 401) {
      await removeItem(DEVICE_ID, ctx);

      if (tokenRetryAttempts > 0) {
        tokenRetryAttempts -= 1;

        return updateAuthToken(tokenParams, ctx, dispatch);
      }
    }

    let errorStatus = 'LOGIN_ERROR';
    if (error.response?.data?.invalidCredentials) errorStatus = 'INVALID_CREDENTIALS';
    if (error.response?.data?.emailRequired) errorStatus = 'MISSING_EMAIL';
    if (error.response?.data?.passwordRequired) errorStatus = 'MISSING_PASSWORD';

    return {
      token: {
        authToken: null,
        authTokenExpires: '',
        loggedIn: false,
        currency: 'GBP',
      },
      error: errorStatus,
    };
  }
};

export const getAuthToken = async (ctx?: NetworkContext, dispatch?: Dispatch): Promise<TokenResponse> => {
  let token: Token | null = null;
  const cachedToken = await getItem(TOKEN_ID, ctx);

  if (cachedToken) {
    token = { ...cachedToken };
  }

  if (token && dayjs().add(2, 'minutes').isBefore(dayjs(token.authTokenExpires, API_DATE_FORMAT))) {
    updateProfile(token, ctx);

    return { token };
  }

  return updateAuthToken(undefined, ctx, dispatch);
};

let getAuthTokenDebounced = debounce(getAuthToken, DEBOUNCE_TIME, {
  leading: true,
  trailing: false,
});

const getDeviceIdDebounced = debounce(getDeviceId, DEBOUNCE_TIME, {
  leading: true,
  trailing: false,
});

export const clearAuthToken = async (ctx?: NetworkContext) => {
  await removeItem(TOKEN_ID, ctx);
};

export const getAuthSignature = async (requestUrl?: string, ctx?: NetworkContext, dispatch?: Dispatch) => {
  // debounce is disabled sever-side to prevent grabbing a token from a another users request
  const tokenResponse = ctx ? await getAuthToken(ctx, dispatch) : await getAuthTokenDebounced(ctx, dispatch);
  const deviceId = ctx ? await getDeviceId(ctx) : await getDeviceIdDebounced(ctx);

  const authToken = tokenResponse ? tokenResponse.token.authToken : '';
  const payload = deviceId + requestUrl;
  const secret = config.clientSecret + authToken;

  const payloadBytes = Utf8.parse(payload);
  const secretBytes = Utf8.parse(secret);

  const signatureBytes = HmacSHA256(payloadBytes, secretBytes);
  const signature = Hex.stringify(signatureBytes);

  return {
    authToken,
    deviceId,
    signature,
    token: tokenResponse?.token,
  };
};
